import { BlobImageConfigContext } from '@ms/yammer-blob-images';
import { getExperimentTreatment } from '@ms/yammer-data/dist/domains/experiments/selectors';
import type { Store } from '@ms/yammer-data/dist/state/types';
import { ExperimentContextProvider } from '@ms/yammer-experiments-context';
import {
  useAndReportExperimentTreatment,
  useExperimentTreatmentWithoutReporting as useExperimentTreatment,
  useReportExperimentTreatmentCallback,
} from '@ms/yammer-hooks/dist/experiments';
import { useIsFeatureRolloutEnabled } from '@ms/yammer-hooks/dist/featureRollouts';
import { LazyRelayAppWrapper } from '@ms/yammer-relay/dist/environment';
import { AdditionalHeadersContext } from '@ms/yammer-teams-qna';
import { PageSectionRefsProvider } from '@ms/yammer-web-components/dist/app/PageSectionRefs';
import { AnnouncerProvider } from '@ms/yammer-web-core-ui/dist/components/Announcer';
import GriffelNoopRendererProvider from '@ms/yammer-web-core-ui/dist/components/GriffelNoopRendererProvider';
import { basenameForTeamsMeetingApp } from '@ms/yammer-web-support/dist/location';
import { getEntityScopeHeaders } from 'domains/teams/actions';
import React, { useMemo } from 'react';
import type { FC } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import type { YammerTeamsEventState } from 'state/types';

import App from '../App';

import { AppError } from './AppError';
import { applyMainStyles } from './Main.styles.legacy';
import { getLazyRelayAppWrapperProps } from './getLazyRelayAppWrapperProps';

const experimentContext = {
  useReportExperimentTreatmentCallback,
  useIsFeatureRolloutEnabled,
  useExperimentTreatment,
  useAndReportExperimentTreatment,
  getExperimentTreatment,
};

export interface MainProps {
  readonly store: Store<YammerTeamsEventState>;
  readonly error?: unknown;
  readonly etag?: string;
}

const Main: FC<MainProps> = ({ store, error, etag }) => {
  const children = error != null ? <AppError error={error} /> : <App />;

  const lazyRelayAppWrapperProps = useMemo(() => getLazyRelayAppWrapperProps(store, etag), [store, etag]);

  const additionalHeaders = useMemo(() => getEntityScopeHeaders(store.getState()), [store]);
  const blobImageConfig = useMemo(() => ({ shouldForceBlobImages: true }), []);

  applyMainStyles();

  return (
    <GriffelNoopRendererProvider>
      <Provider store={store}>
        <AdditionalHeadersContext.Provider value={additionalHeaders}>
          <BlobImageConfigContext.Provider value={blobImageConfig}>
            <ExperimentContextProvider {...experimentContext}>
              <LazyRelayAppWrapper {...lazyRelayAppWrapperProps}>
                <HelmetProvider>
                  <BrowserRouter basename={basenameForTeamsMeetingApp}>
                    <PageSectionRefsProvider>
                      <AnnouncerProvider>{children}</AnnouncerProvider>
                    </PageSectionRefsProvider>
                  </BrowserRouter>
                </HelmetProvider>
              </LazyRelayAppWrapper>
            </ExperimentContextProvider>
          </BlobImageConfigContext.Provider>
        </AdditionalHeadersContext.Provider>
      </Provider>
    </GriffelNoopRendererProvider>
  );
};

export default Main;
